import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useEffect, useMemo, useState } from 'react';

import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { useTimeoutFn } from '@sorare/core/src/hooks/animations/useTimeoutFn';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';

import type {
  PrimaryOfferBuyerQuery,
  PrimaryOfferBuyerQueryVariables,
  usePollPrimaryOfferBuyer_primaryOffer,
} from './__generated__/usePollPrimaryOfferBuyer.graphql.ts';

const PRIMARY_OFFER_BUYER_QUERY = gql`
  query PrimaryOfferBuyerQuery($id: String!) {
    tokens {
      primaryOffer(id: $id) {
        id
        buyer {
          slug
        }
      }
    }
  }
` as TypedDocumentNode<PrimaryOfferBuyerQuery, PrimaryOfferBuyerQueryVariables>;

export const usePollPrimaryOfferBuyer = (
  primaryOffer: usePollPrimaryOfferBuyer_primaryOffer | undefined,
  cb: () => void
) => {
  const [polling, setPolling] = useState(false);
  const { currentUser } = useCurrentUserContext();
  const primaryOfferId = idFromObject(primaryOffer?.id) || '';
  useQuery(PRIMARY_OFFER_BUYER_QUERY, {
    variables: {
      id: primaryOfferId,
    },
    pollInterval: polling ? 1000 : 0,
    skip: !polling || !primaryOfferId,
  });

  const userIsBuyer = useMemo(
    () => primaryOffer?.buyer && primaryOffer.buyer.slug === currentUser?.slug,
    [primaryOffer?.buyer, currentUser]
  );

  const startPolling = () => {
    setPolling(true);
  };
  const stopPolling = () => {
    setPolling(false);
  };
  useTimeoutFn(stopPolling, polling ? 10000 : null);

  useEffect(() => {
    if (userIsBuyer && polling) {
      cb();
      // eslint-disable-next-line react-hooks/set-state-in-effect
      stopPolling();
    }
  });

  return [polling, startPolling] as const;
};

usePollPrimaryOfferBuyer.fragments = {
  primaryOffer: gql`
    fragment usePollPrimaryOfferBuyer_primaryOffer on TokenPrimaryOffer {
      id
      buyer {
        slug
      }
    }
  ` as TypedDocumentNode<usePollPrimaryOfferBuyer_primaryOffer>,
};
